import { type VariantProps, cva } from 'class-variance-authority';
import { twMerge } from 'tailwind-merge';

import type { CommonProps } from '../../types';
import { ReactElement, useEffect, useRef, useState, type JSX } from 'react';

export interface EventDetailWrapperProps
    extends CommonProps,
        VariantProps<typeof EventDetailWrapperStyles> {
    type: 'image' | 'content' | 'timing';
    children: ReactElement;
}

export const EventDetailWrapperStyles = cva(
    'relative group overflow-hidden rounded-lg bg-gray-500 md:max-h-[232px]'
);

export const EventCardTextStyles = cva('flex flex-col p-8 h-full');

export const EventDetailWrapper = ({
    className,
    children,
    type,
    testId = 'event-detail-tile',
    ...props
}: EventDetailWrapperProps): JSX.Element => {
    const scrollableRef = useRef<HTMLDivElement | null>(null);

    const hasShadow = scrollableRef?.current
        ? scrollableRef.current?.clientHeight <
          scrollableRef.current?.scrollHeight
        : false;

    const [showShadow, setShowShadow] = useState(hasShadow);

    const handleScroll = () => {
        const scrollableDiv = scrollableRef.current;
        if (scrollableDiv) {
            const scrollTop = scrollableDiv.scrollTop;
            const scrollHeight = scrollableDiv.scrollHeight;
            const clientHeight = scrollableDiv.clientHeight;

            setShowShadow(scrollTop + clientHeight < scrollHeight);
        }
    };

    useEffect(() => {
        const scrollableDiv = scrollableRef.current;
        setShowShadow(hasShadow);

        // Add event listener for scroll
        if (scrollableDiv) {
            scrollableDiv.addEventListener('scroll', handleScroll);
        }

        // Clean up the event listener on component unmount
        return () => {
            if (scrollableDiv) {
                scrollableDiv.removeEventListener('scroll', handleScroll);
            }
        };
    }, [hasShadow]);

    return (
        <div
            className={twMerge(EventDetailWrapperStyles({ className }))}
            {...props}
            data-testid={testId}
        >
            {type === 'content' ? (
                <div className={twMerge(EventCardTextStyles())}>{children}</div>
            ) : type === 'image' ? (
                <div>{children}</div>
            ) : (
                <div
                    ref={scrollableRef}
                    className={`pr-2 md:max-h-[168px] md:overflow-auto ${
                        showShadow && 'fade-longTile'
                    }`}
                >
                    {children}
                </div>
            )}
        </div>
    );
};
